import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import type { ApolloClient, DocumentNode } from '@apollo/client';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { downloadExcelFile } from '@utils/excel/download-json-excel-service';
import { useDrawerSubmission } from '@common/hooks';
import FormattingService from '@core/services/FormattingService';

import {
  buildExportAmendmentsDrawerSections,
  ALL,
  exportAmendmentsExcelColumnWidths,
  exportAmendmentsExcelHeaderGroups,
  exportAmendmentsExcelHeaders,
  exportAmendmentsExcelStyles,
  IExportAmendmentsFilters
} from '@common/constants/drawer.constants';
import {
  useGetAmendments,
  useGetExportAmendmentsLOVs,
  useSearchUsersForExport
} from '@common/slices/drawer.slice';

import DateService from 'src/core/services/DateService';
import {
  GetAmendmentsResponse,
  GetAmendmentsVariables,
  IBaseDrawerProps
} from '@common/types';
import { RootState } from '@core/redux/store';
import { useSelector } from 'react-redux';
import { capitalizeFirstLetter } from '@utils/formatting-utils';

const buildExportAmendmentsLovs = (lovData: any) => {
  const statusEnum =
    lovData?.AmendmentStatusList?.enumValues?.map((e: any) => e.name) || [];

  const Status: Record<string, string> = { [ALL]: 'All' };
  statusEnum.forEach((v: string) => {
    Status[v] = FormattingService.formatEnumToDisplay(v) ?? v;
  });

  return { Status };
};

const normalizeStatusesForBackend = (values: string[] | null) => {
  if (!values?.length) return null;

  return values.map((s) =>
    FormattingService.formatEnumToBackend(String(s).toLowerCase())
  );
};

const normalizeMulti = (v: string[]): string[] => {
  if (!v || v[0] === ALL) return [];
  return Array.isArray(v) ? v : [v];
};

const fetchAllAmendmentsForExport = async (
  getAmendments: (options?: {
    variables: GetAmendmentsVariables;
  }) => Promise<{ data: GetAmendmentsResponse }>,
  vars: {
    effectiveOnStart: string;
    effectiveOnEnd: string;
    createdOnStart: string;
    createdOnEnd: string;
    statuses: string[];
    createdBy: string[];
  }
) => {
  const pageSize = 500;
  let currentPage = 1;
  const all: any[] = [];
  let hasMore = true;

  while (hasMore) {
    const { data } = await getAmendments({
      variables: {
        pagination: {
          pageNumber: currentPage,
          pageSize: pageSize
        },
        orderBy: {
          descending: true,
          attribute: 'createdOn'
        },
        createdOnStart: vars.createdOnStart,
        createdOnEnd: vars.createdOnEnd,
        effectiveOnStart: vars.effectiveOnStart,
        effectiveOnEnd: vars.effectiveOnEnd,
        statuses: vars.statuses,
        createdBy: vars.createdBy
      }
    });

    const items = data?.production?.searchAmendments?.items || [];
    all.push(...items);

    hasMore = items.length === pageSize;
    currentPage += 1;
  }

  return all;
};

const mapAmendmentsToExcelData = (items: any[]) =>
  items.map((a: any) => ({
    amendmentNumber: a?.referenceNumber ?? '',
    amendmentType:
      FormattingService.formatEnumToDisplay(a?.amendmentType) ?? '',
    amendmentReason: a?.amendmentReason ?? '',
    amendmentEffectiveDate: a?.amendmentEffectiveDate ?? '',
    plan: a?.policy?.plan
      ? `${a.policy.plan.code} - ${a.policy.plan.name}`
      : '',
    user: a?.policy?.user
      ? `${a.policy.user.code} - ${[a.policy.user.firstName, a.policy.user.middleName, a.policy.user.lastName].filter(Boolean).join(' ')}`
      : '',
    broker: a?.policy?.mainBusinessPartner
      ? `${a.policy.mainBusinessPartner.code} - ${a.policy.mainBusinessPartner.fullName}`
      : '',
    policyNumber: a?.policy?.policyNumber ?? '',
    policyEffectiveDate: a?.policy?.policyEffectiveDate ?? '',
    policyExpiryDate: a?.policy?.policyExpiryDate ?? '',
    policyIssueDate: a?.policy?.policyIssueDate ?? '',
    currency: a?.policy?.policyCurrency?.title ?? '',
    status: FormattingService.formatEnumToDisplay(a?.status) ?? '',
    statusReason: a?.policy?.statusReason ?? ''
  }));

const formatDateRange = (range: any[]) => {
  if (!range?.[0] || !range?.[1]) return null;
  return [
    DateService.formatDateTimeBackend(range[0]),
    DateService.formatDateTimeBackend(range[1])
  ];
};

const ExportAmendmentsDrawer: React.FC<IBaseDrawerProps> = ({ onSuccess }) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const [values, setValues] = useState<IExportAmendmentsFilters>({
    user: [ALL],
    status: [ALL],
    amendmentEffectiveDateRange: [],
    createdOn: []
  });

  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [sections, setSections] = useState<IDynamicFormWidgetSection[]>([]);

  const [getAmendments] = useGetAmendments.useLazy();
  const { data: lovData } = useGetExportAmendmentsLOVs.useQuery();
  const [searchUsers] = useSearchUsersForExport.useLazy();

  useEffect(() => {
    const built = buildExportAmendmentsLovs(lovData);
    setLovs(built);
    setSections(buildExportAmendmentsDrawerSections(values, built));
  }, [lovData]);

  const submitForm = async (): Promise<void> => {
    const [data] = normalizeDynamicFormDrawerValues(sections, values);
    const selectedUsers = normalizeMulti(data.user);
    const selectedStatuses = normalizeStatusesForBackend(
      normalizeMulti(data.status)
    );

    const effectiveDateRange = formatDateRange(
      data.amendmentEffectiveDateRange
    );
    const createdOnRange = formatDateRange(data.createdOn);

    const items = await fetchAllAmendmentsForExport(getAmendments, {
      effectiveOnStart: effectiveDateRange?.[0] || null,
      effectiveOnEnd: effectiveDateRange?.[1] || null,
      createdOnStart: createdOnRange?.[0] || null,
      createdOnEnd: createdOnRange?.[1] || null,
      statuses: selectedStatuses || [],
      createdBy: selectedUsers || []
    });

    if (!items.length) {
      toast.error(
        <ToastErrorMessage>
          No amendments found for the selected filters
        </ToastErrorMessage>
      );
      return;
    }

    const excelData = mapAmendmentsToExcelData(items);

    downloadExcelFile({
      data: excelData,
      filename: 'Redbridge_Amendments',
      headers: exportAmendmentsExcelHeaders,
      sheetName: 'Sheet1',
      groupHeaders: exportAmendmentsExcelHeaderGroups,
      styles: exportAmendmentsExcelStyles,
      columnWidths: exportAmendmentsExcelColumnWidths,
      freezeHeaderRows: 2,
      headerRowHeight: 14,
      groupHeaderRowHeight: 18,
      separator: { enabled: true, applyTo: 'headerOnly', style: 'medium' }
    });
    onSuccess();
  };

  const handleChange = (
    fieldName: keyof IExportAmendmentsFilters,
    value: any,
    _sectionIndex: number,
    updatedValues: IExportAmendmentsFilters
  ) => {
    const newValues = { ...updatedValues, [fieldName]: value };
    setValues(newValues);
    setSections(buildExportAmendmentsDrawerSections(newValues, lovs));
  };

  const handleAutocompleteSearch = async (
    fieldName: string,
    query: string,
    pagination: { pageNumber: number; pageSize: number }
  ) => {
    if (fieldName === 'user') {
      const result = await searchUsers({
        variables: {
          keyword: query || '',
          pageNumber: pagination.pageNumber,
          pageSize: pagination.pageSize
        },
        fetchPolicy: 'no-cache'
      });
      const items = result?.data?.core?.searchUsers?.items ?? [];
      const options: Record<string, string> = { [ALL]: 'All' };
      items.forEach((u: any) => {
        if (u?.id) {
          const fullName = [u.firstName, u.middleName, u.lastName]
            .filter(Boolean)
            .join(' ');
          options[String(u.id)] = `${u.code ?? ''} - ${fullName}`;
        }
      });
      return { options, totalCount: items.length + 1 };
    }

    const allOptions = lovs[fieldName] ?? {};
    const filtered = Object.fromEntries(
      Object.entries(allOptions).filter(([, label]) =>
        label.toLowerCase().includes(query.toLowerCase())
      )
    );
    const entries = Object.entries(filtered);
    const start = (pagination.pageNumber - 1) * pagination.pageSize;

    return {
      options: Object.fromEntries(
        entries.slice(start, start + pagination.pageSize)
      ),
      totalCount: entries.length
    };
  };

  const handleInitialMultiAutocompleteSearch = async (
    fieldName: string,
    vals: string[]
  ) => {
    if (fieldName === 'user') {
      const options: Record<string, string> = {};
      if (vals.includes(ALL)) options[ALL] = 'All';

      return { options, totalCount: Object.keys(options).length };
    }

    const allOptions = lovs[fieldName] ?? {};
    const options = Object.fromEntries(
      vals.filter((v) => allOptions[v]).map((v) => [v, allOptions[v]])
    );

    return { options, totalCount: Object.keys(options).length };
  };

  return (
    <DynamicFormWidget
      onChange={handleChange}
      sections={sections}
      submitButtonState={submitButtonState}
      onSubmit={submitForm}
      onAutocompleteSearch={handleAutocompleteSearch}
      initialMultiAutocompleteSearch={handleInitialMultiAutocompleteSearch}
    />
  );
};

export { ExportAmendmentsDrawer };
